import { sectionLabels } from "../section-labels";
import { BUYBOX_VARIANT_OPTIONS } from "./buybox-presets";
/**
 * Product buy box — locked. The PDP's info side: breadcrumb, rating, description
 * placement, the buy-area treatment (CTA style, free-shipping bar, upsell card,
 * trust badges), the mobile + desktop sticky buy bars, and the per-family PDP
 * typography (title / price / button). Split out of the legacy `product-details`
 * section so the seller can configure the buy area independently of the gallery.
 *
 * All `showIf` references stay WITHIN this section:
 *   - `stickyBarStyle` → `stickyAddToCart`
 *   - `stickyBuyBarBehavior` / `stickyBuyBarStyle` → `stickyBuyBarDesktop`
 *
 * Lives under `templates.product` only.
 */
export const productBuyboxSchema = {
    type: "product-buybox",
    name: sectionLabels["product-buybox"],
    description: {
        ar: "منطقة اسم المنتج والسعر وأزرار الشراء وشارات الثقة.",
        en: "The PDP buy area: name, price, CTA, trust badges, sticky bars.",
    },
    locked: true,
    criticalForSeo: true,
    maxPerTemplate: 1,
    availableOnTemplates: ["product"],
    settings: [
        // Buy-box VARIANT — pick another family's whole buy-box look as the base
        // (title / price / CTA / stepper / badges / description). `custom` (default)
        // authors every field below yourself; any family value spreads that family's
        // preset (see `buybox-presets.ts`) then lets the fields below override it, so
        // an editorial store can wear the pearl buy box and still tweak from there.
        {
            // NB: intentionally `buyboxVariant`, NOT `variant`. The PDP flattens all
            // five section settings into one namespace (page.tsx / BuyerProductDetails),
            // and the gallery section already owns `variant` (its layout picker). A
            // bare `variant` here collided with it — picking a buy-box variant clobbered
            // the gallery layout. Keep this key gallery-disjoint.
            id: "buyboxVariant",
            type: "select",
            label: { ar: "نوع منطقة الشراء", en: "Buy box variant" },
            helpText: {
                ar: "ابدأ من شكل منطقة الشراء الخاص بأي ثيم، وعدّل بعدها أي إعداد.",
                en: "Start from any theme's buy-box look, then override any setting below.",
            },
            options: BUYBOX_VARIANT_OPTIONS,
            default: "custom",
        },
        {
            id: "breadcrumbStyle",
            type: "select",
            label: { ar: "شكل مسار التنقّل", en: "Breadcrumb style" },
            helpText: {
                ar: 'إما بدون مسار، أو مسار مختصر (تسوق / المنتج) بحروف كبيرة، أو مسار كامل بالفئة (الرئيسية | الفئة | المنتج).',
                en: 'None, a short uppercase path (Shop / product), or a full path including the category (Home | Category | product).',
            },
            options: [
                { value: "none", label: { ar: "بدون", en: "None" } },
                { value: "mono", label: { ar: "مختصر بحروف كبيرة", en: "Short (uppercase)" } },
                {
                    value: "path",
                    label: { ar: "كامل مع الفئة", en: "Full path with category" },
                },
            ],
            default: "mono",
        },
        {
            id: "showRating",
            type: "checkbox",
            label: { ar: "إظهار تقييم النجوم", en: "Show star rating" },
            helpText: {
                ar: "تقييم نجوم صغير جنب اسم المنتج في عمود المعلومات.",
                en: "Inline star rating next to the name in the info column.",
            },
            default: false,
        },
        {
            id: "showCategoryEyebrow",
            type: "checkbox",
            label: { ar: "إظهار اسم القسم فوق العنوان", en: "Show category above title" },
            helpText: {
                ar: "سطر صغير باسم القسم فوق اسم المنتج. أطفئه لو مسار التنقّل بيعرضه بالفعل.",
                en: "A small category line above the product name. Turn off when the breadcrumb already shows it.",
            },
            default: true,
        },
        {
            id: "categoryEyebrowStyle",
            type: "select",
            label: { ar: "شكل اسم القسم", en: "Category eyebrow style" },
            helpText: {
                ar: "نص عادي، أو شارة ملوّنة.",
                en: "Plain uppercase text, or a filled accent pill.",
            },
            options: [
                { value: "text", label: { ar: "نص", en: "Text" } },
                { value: "pill", label: { ar: "شارة ملوّنة", en: "Accent pill" } },
            ],
            default: "text",
            showIf: { showCategoryEyebrow: true },
        },
        {
            id: "pdpStepperBorder",
            type: "select",
            label: { ar: "لون إطار خانة الكمية", en: "Quantity box border" },
            helpText: {
                ar: "إطار خفيف، أو غامق واضح، أو بلون التمييز (بيتغير حسب التصميم).",
                en: "A faint hairline, a defined ink/black, or the accent colour (varies by theme).",
            },
            options: [
                { value: "hairline", label: { ar: "خفيف", en: "Hairline" } },
                { value: "ink", label: { ar: "غامق", en: "Ink" } },
                { value: "accent", label: { ar: "لون التمييز", en: "Accent" } },
            ],
            default: "hairline",
        },
        {
            id: "buyActionsLayout",
            type: "select",
            label: { ar: "ترتيب أزرار الشراء", en: "Buy actions layout" },
            helpText: {
                ar: "الكمية جنب زر الإضافة، أو الكمية بعرض كامل في سطر لوحدها فوق الأزرار.",
                en: "The quantity beside Add-to-cart, or a full-width quantity on its own row above the buttons.",
            },
            options: [
                { value: "inline", label: { ar: "الكمية بجانب الزر", en: "Inline stepper" } },
                {
                    value: "stacked",
                    label: { ar: "كمية بعرض كامل", en: "Full-width stepper row" },
                },
            ],
            default: "inline",
        },
        {
            id: "showStockBadge",
            type: "checkbox",
            label: { ar: 'إظهار شارة "متوفر"', en: 'Show "In stock" badge' },
            helpText: {
                ar: 'شارة "متوفر" الخضراء جنب السعر.',
                en: 'The green "In stock" pill next to the price.',
            },
            default: true,
        },
        {
            id: "showSaveBadge",
            type: "checkbox",
            label: { ar: 'إظهار شارة الخصم "وفّر"', en: 'Show "Save -X%" badge' },
            helpText: {
                ar: 'شارة نسبة الخصم جنب السعر. أطفئها لو المتجر بيعرض السعر المشطوب فقط.',
                en: 'The discount-percent pill next to the price. Turn off to show only the crossed-out original price.',
            },
            default: true,
        },
        {
            id: "saveBadgePlacement",
            type: "select",
            label: { ar: "مكان شارة الخصم", en: "Discount badge placement" },
            helpText: {
                ar: 'جنب السعر (وفّر -X%)، أو شارة حمراء فوق العنوان (X% off).',
                en: 'Next to the price ("Save -X%"), or a red pill above the title ("X% off").',
            },
            options: [
                { value: "price", label: { ar: "جنب السعر", en: "Next to price" } },
                {
                    value: "above-title",
                    label: { ar: "فوق العنوان", en: "Above title" },
                },
                {
                    value: "price-right",
                    label: { ar: "يمين سطر السعر", en: "Far right of price row" },
                },
            ],
            default: "price",
            showIf: { showSaveBadge: true },
        },
        {
            id: "showShortDescription",
            type: "checkbox",
            label: { ar: "إظهار الوصف المختصر أعلى", en: "Show short description at top" },
            helpText: {
                ar: "فقرة الوصف المختصر تحت العنوان مباشرةً في عمود الشراء.",
                en: "The short-description paragraph right under the title in the buy column.",
            },
            default: true,
        },
        {
            id: "descriptionPlacement",
            type: "select",
            label: { ar: "مكان وصف المنتج", en: "Description placement" },
            helpText: {
                ar: "الوصف في أكورديون تحت المنتج، ولا جوه تفاصيل المنتج.",
                en: "Show the description in an accordion below, or inside the product details.",
            },
            options: [
                {
                    value: "accordion-below",
                    label: { ar: "أكورديون تحت", en: "Accordion below" },
                },
                { value: "in-details", label: { ar: "جوه التفاصيل", en: "In details" } },
                {
                    value: "accordion-grouped",
                    label: {
                        ar: "أكورديون موحّد مع السياسات",
                        en: "Grouped with policies",
                    },
                },
                {
                    value: "tabs",
                    label: {
                        ar: "تبويبات (وصف + سياسات)",
                        en: "Pill tabs (description + policies)",
                    },
                },
                {
                    value: "collapse",
                    label: {
                        ar: "أكورديون وصف تحت المعرض",
                        en: "Description collapse under gallery (EO catalog)",
                    },
                },
            ],
            default: "accordion-below",
        },
        // --- Buy area treatment (shared across all variants) -----------------
        {
            id: "ctaTreatment",
            type: "select",
            label: { ar: "شكل أزرار الشراء", en: "Buy button treatment" },
            helpText: {
                ar: 'إما زرين مملوءين، أو "أضف للسلة" بإطار و"اشترِ الآن" مملوء.',
                en: 'Both buttons filled, or an outlined "Add to cart" above a solid "Buy now".',
            },
            options: [
                { value: "solid", label: { ar: "زرين مملوءين", en: "Both solid" } },
                {
                    value: "outline-add",
                    label: { ar: "إضافة بإطار + شراء مملوء", en: "Outline add + solid buy" },
                },
            ],
            default: "solid",
        },
        {
            /**
             * The buy button's prominence, where a seller actually looks for it.
             *
             * The brand panel's «حجم الزر» does reach these buttons now (they read
             * `--pdp-btn-*-theme`), but "make the buy button bigger" is a decision
             * about THIS button, not about every button in the store — and the raw
             * `pdpButtonSize` / `pdpButtonPadding` fields that could do it ask the
             * seller for `10.4px 28px`.
             */
            id: "pdpButtonScale",
            type: "select",
            label: { ar: "حجم زر الشراء", en: "Buy button size" },
            helpText: {
                ar: "سيبه «زي باقي المتجر» عشان ياخد حجم الأزرار العام، أو كبّره هنا بس.",
                en: "Leave it following the store's button size, or make these buttons bigger on their own.",
            },
            options: [
                { value: "inherit", label: { ar: "زي باقي المتجر", en: "Same as the store" } },
                { value: "lg", label: { ar: "كبير", en: "Large" } },
                { value: "xl", label: { ar: "عملاق", en: "Extra large" } },
            ],
            default: "inherit",
        },
        // --- Share control style --------------------------------------------
        {
            id: "shareStyle",
            type: "select",
            label: { ar: "شكل المشاركة", en: "Share style" },
            helpText: {
                ar: 'زر أيقونة واحد، أو صف "شارك:" بروابط شبكات (فيسبوك/إكس/بينترست/لينكدإن).',
                en: 'A single icon button, or a labelled "Share:" row with network links (Facebook/X/Pinterest/LinkedIn).',
            },
            options: [
                { value: "button", label: { ar: "زر أيقونة", en: "Icon button" } },
                { value: "row", label: { ar: 'صف "شارك:"', en: '"Share:" row' } },
                {
                    value: "tiles",
                    label: { ar: "مربّعات (بدون عنوان)", en: "Square tiles (no label)" },
                },
            ],
            default: "button",
        },
        // REMOVED: the free-shipping progress bar and its threshold — see the same
        // note in `product-details.ts`. The number was theme-local and checkout
        // never read it, so the page promised free shipping the order then charged
        // for. Real free shipping is a promotion, not a theme setting.
        // --- In-buy-area cross-sell / upsell card ---------------------------
        {
            id: "showUpsellCard",
            type: "checkbox",
            label: {
                ar: "بطاقة منتجات مقترحة في منطقة الشراء",
                en: "Cross-sell card in the buy area",
            },
            helpText: {
                ar: "بطاقة صغيرة تحت أزرار الشراء تعرض منتجات مقترحة واحد ورا التاني.",
                en: "A compact card under the buy buttons stepping through suggested products.",
            },
            default: false,
        },
        // REMOVED with it: `buyExtrasLayout`. It chose between merging the
        // free-shipping bar with the upsell card or keeping them apart — with only
        // the upsell left there is nothing to merge, so the control could not
        // change anything a seller would see.
        // REMOVED: `showStoreHighlights`. The trust strip is no longer theme copy
        // to switch on and off — it states the store's own operational truth (COD,
        // the return/exchange windows) or the seller's own highlights, and renders
        // only when there is something true to say. A store that promises nothing
        // gets no strip; a toggle over that is a control with nothing to control.
        {
            id: "sectionCardStyle",
            type: "select",
            label: { ar: "شكل منطقة الشراء", en: "Buy-area container" },
            helpText: {
                ar: "عادي (بدون إطار) أو بطاقات بيضاء بحواف دائرية فوق خلفية الصفحة.",
                en: "Plain (no frame) or white rounded cards on the page background.",
            },
            options: [
                { value: "none", label: { ar: "عادي", en: "Plain" } },
                { value: "card", label: { ar: "بطاقات", en: "Rounded cards" } },
            ],
            default: "none",
        },
        {
            id: "stickyAddToCart",
            type: "checkbox",
            // The sticky buy bar (`BuyerProductStickyBar`) is `lg:hidden` — phone +
            // tablet only; desktop keeps the inline CTA stack. This toggle and its
            // style below therefore only affect mobile + tablet.
            appliesTo: ["mobile", "tablet"],
            label: {
                ar: 'تثبيت زر "أضف للسلة" أسفل الشاشة على الجوال',
                en: "Sticky add-to-cart on mobile",
            },
            default: true,
        },
        {
            id: "stickyBarStyle",
            type: "select",
            appliesTo: ["mobile", "tablet"],
            label: { ar: "شكل الشريط السفلي", en: "Sticky bar style" },
            helpText: {
                ar: 'شكل شريط "أضف للسلة" المثبت أسفل الشاشة على الجوال.',
                en: "How the mobile sticky add-to-cart bar looks.",
            },
            options: [
                {
                    value: "bar-qty",
                    label: { ar: "كمية + إضافة للسلة", en: "Quantity + Add to cart" },
                },
                {
                    value: "bar-price",
                    label: { ar: "السعر + إضافة للسلة", en: "Price + Add to cart" },
                },
            ],
            default: "bar-qty",
            // A9: the sticky bar style only matters when the sticky bar is enabled.
            showIf: { stickyAddToCart: true },
        },
        {
            id: "stickyBuyBarDesktop",
            type: "checkbox",
            // The desktop sticky buy bar (`BuyerProductDesktopBuyBar`) is `lg:block` —
            // desktop only; phones/tablets use the `stickyAddToCart` bar above. This
            // mirrors EasyOrders' platform-rendered desktop buy bar (the PDP parity gap).
            appliesTo: ["desktop"],
            label: {
                ar: "شريط شراء ثابت أسفل الشاشة على الكمبيوتر",
                en: "Sticky buy bar on desktop",
            },
            helpText: {
                ar: "شريط سفلي بصورة المنتج والسعر والكمية وزر الشراء على شاشات الكمبيوتر.",
                en: "A bottom bar with the product thumb, price, quantity and Buy-now on desktop.",
            },
            default: false,
        },
        {
            id: "stickyBuyBarBehavior",
            type: "select",
            appliesTo: ["desktop"],
            label: { ar: "ظهور الشريط", en: "Bar visibility" },
            helpText: {
                ar: "دايمًا ظاهر، أو يظهر بعد ما ينزل الزائر لتحت.",
                en: "Always visible, or revealed after the shopper scrolls down.",
            },
            options: [
                { value: "always-on", label: { ar: "دايمًا ظاهر", en: "Always visible" } },
                { value: "on-scroll", label: { ar: "يظهر عند النزول", en: "On scroll" } },
            ],
            default: "on-scroll",
            showIf: { stickyBuyBarDesktop: true },
        },
        {
            id: "stickyBuyBarStyle",
            type: "select",
            appliesTo: ["desktop"],
            label: { ar: "شكل الشريط", en: "Bar style" },
            helpText: {
                ar: "شريط بعرض الشاشة، أو بطاقة عائمة في الركن.",
                en: "A full-width bar, or a floating card anchored in the corner.",
            },
            options: [
                {
                    value: "full-width-bar",
                    label: { ar: "شريط كامل العرض", en: "Full-width bar" },
                },
                {
                    value: "centered-bar",
                    label: { ar: "شريط موسّط", en: "Centered bar" },
                },
                {
                    value: "centered-sharp",
                    label: { ar: "شريط موسّط حاد", en: "Centered bar (sharp)" },
                },
                {
                    value: "stacked-bar",
                    label: { ar: "شريط بزر كامل العرض", en: "Stacked bar" },
                },
                {
                    value: "floating-card",
                    label: { ar: "بطاقة عائمة", en: "Floating card" },
                },
            ],
            default: "full-width-bar",
            showIf: { stickyBuyBarDesktop: true },
        },
        // --- Per-family PDP typography (EO parity). CSS values applied as vars on
        //     the PDP root; empty ⇒ the component's built-in default (legacy stores
        //     unchanged). Populated per theme from the CSS audit. ----------------
        {
            id: "pdpTitleSize",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "حجم عنوان المنتج", en: "Product title size" },
            helpText: { ar: "مثال: 22px", en: "CSS size, e.g. 22px. Empty = default." },
            default: "",
        },
        {
            id: "pdpTitleWeight",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "سماكة العنوان", en: "Product title weight" },
            helpText: { ar: "مثال: 400", en: "CSS weight, e.g. 400. Empty = default." },
            default: "",
        },
        {
            id: "pdpTitleTransform",
            type: "select",
            label: { ar: "حالة أحرف العنوان", en: "Product title case" },
            options: [
                { value: "none", label: { ar: "عادي", en: "Normal" } },
                { value: "uppercase", label: { ar: "أحرف كبيرة", en: "UPPERCASE" } },
            ],
            default: "none",
        },
        {
            id: "pdpTitleTracking",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "تباعد أحرف العنوان", en: "Product title tracking" },
            helpText: { ar: "مثال: 0.18em", en: "CSS letter-spacing. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonRadius",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "تدوير أزرار المنتج", en: "Product button radius" },
            helpText: { ar: "مثال: 0px", en: "CSS radius, e.g. 0px. Empty = theme default." },
            default: "",
        },
        // --- Per-family PDP price typography (EO parity). Empty ⇒ the component's
        //     built-in default (22px/600/--fg, formerly monospace). Populated per
        //     family from the CSS audit; drops the legacy mono face on the price. --
        {
            id: "pdpPriceSize",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "حجم السعر", en: "Price size" },
            helpText: { ar: "مثال: 14px", en: "CSS size, e.g. 14px. Empty = default." },
            default: "",
        },
        {
            id: "pdpPriceWeight",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "سماكة السعر", en: "Price weight" },
            helpText: { ar: "مثال: 700", en: "CSS weight, e.g. 700. Empty = default." },
            default: "",
        },
        {
            id: "pdpPriceColor",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "لون السعر", en: "Price color" },
            helpText: { ar: "مثال: #111827", en: "CSS color. Empty = theme --fg." },
            default: "",
        },
        // --- Per-family PDP buy-button typography (EO parity). Empty ⇒ component
        //     defaults (15px/600, no transform, 0 20px padding, accent border). ------
        {
            id: "pdpButtonSize",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "حجم خط الأزرار", en: "Button font size" },
            helpText: { ar: "مثال: 13px", en: "CSS size, e.g. 13px. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonWeight",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "سماكة خط الأزرار", en: "Button font weight" },
            helpText: { ar: "مثال: 500", en: "CSS weight, e.g. 500. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonTransform",
            type: "select",
            label: { ar: "حالة أحرف الأزرار", en: "Button text case" },
            options: [
                { value: "none", label: { ar: "عادي", en: "Normal" } },
                { value: "uppercase", label: { ar: "أحرف كبيرة", en: "UPPERCASE" } },
                { value: "capitalize", label: { ar: "أول حرف كبير", en: "Capitalize" } },
            ],
            default: "none",
        },
        {
            id: "pdpButtonPadding",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "حشو الأزرار", en: "Button padding" },
            helpText: { ar: "مثال: 10.4px 28px", en: "CSS padding. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonBorderColor",
            styling: true,
            authorOnly: true,
            type: "text",
            label: { ar: "لون إطار زر الإضافة", en: "Add-to-cart border color" },
            helpText: { ar: "مثال: #D8D8D8", en: "CSS color for the outline Add-to-cart border. Empty = accent." },
            default: "",
        },
    ],
};
//# sourceMappingURL=product-buybox.js.map